import type { TFunction } from "i18next";
import type { CSSProperties, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { useNotificationTargetUri } from "@/hooks/useNotificationTargetUri";
import { useRouteRefresh } from "@/hooks/useRouteRefresh";
import { isCurrentPath } from "@/utils/routeRefresh";
import { BulkOperationStatus } from "@/gql/graphql";
import DataListItemText from "./DataListItemText";
import type {
  BulkShipToContactNotificationCopySpec,
  BulkShipToContactNotificationData,
  BulkShipToContactNotificationMode,
  BulkShipToContactNotificationProps,
  BulkShipToContactTitleKey,
} from "../types";

const viewDetailsButtonSx: CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  marginLeft: "0.25rem",
  color: "var(--modus-wc-color-trimble-blue)",
  textDecoration: "underline",
  cursor: "pointer",
  font: "inherit",
  verticalAlign: "baseline",
};

function getBulkShipToContactNotificationMode(
  data: BulkShipToContactNotificationData
): BulkShipToContactNotificationMode {
  const { bulkOperationStatus, failureCount, successCount } = data;
  if (bulkOperationStatus === BulkOperationStatus.Processing) {
    return "processing";
  }
  if (
    bulkOperationStatus === BulkOperationStatus.Failed ||
    (successCount === 0 && failureCount > 0)
  ) {
    return "failed";
  }
  if (failureCount > 0 && successCount > 0) {
    return "partial";
  }
  return "success";
}

const BULK_SHIP_TO_CONTACT_NOTIFICATION_COPY: Record<
  BulkShipToContactNotificationMode,
  BulkShipToContactNotificationCopySpec
> = {
  processing: {
    titleKey: "title_processing",
    getDescription: (t) =>
      t("notifications.bulk_ship_to_contact.description_processing"),
  },
  failed: {
    titleKey: "title_failed",
    getDescription: (t, d) =>
      t("notifications.bulk_ship_to_contact.description_failed", {
        count: d.totalCount,
        failureCount: d.failureCount,
      }),
  },
  partial: {
    titleKey: "title_partial",
    getDescription: (t, d) =>
      t("notifications.bulk_ship_to_contact.description_partial", {
        successCount: d.successCount,
        failureCount: d.failureCount,
      }),
  },
  success: {
    titleKey: "title_success",
    getDescription: (t, d) =>
      t("notifications.bulk_ship_to_contact.description_all_success", {
        count: d.totalCount,
      }),
  },
};

function bulkShipToContactPrimaryText(
  customerName: string,
  titleKey: BulkShipToContactTitleKey,
  t: TFunction
): string {
  if (customerName !== "") {
    return t(`notifications.bulk_ship_to_contact.${titleKey}_with_customer`, {
      customerName,
    });
  }
  return t(`notifications.bulk_ship_to_contact.${titleKey}`);
}

const BulkShipToContactNotification = (
  props: BulkShipToContactNotificationProps
) => {
  const { data, contentUri, onViewDetails } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const refreshCurrentRoute = useRouteRefresh();
  const location = useLocation();
  const targetUri = useNotificationTargetUri(contentUri);
  const customerName = (data.customerName ?? "").trim();

  const handleViewDetailsClick = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onViewDetails?.();
    if (isCurrentPath(targetUri, location.pathname)) {
      refreshCurrentRoute();
      return;
    }
    navigate(targetUri);
  };

  const viewDetails = (
    <button
      type="button"
      aria-label={t("notifications.bulk_ship_to_contact.view_details")}
      onClick={handleViewDetailsClick}
      style={viewDetailsButtonSx}
    >
      {t("notifications.bulk_ship_to_contact.view_details")}
    </button>
  );

  const bulkOperationResultCategory =
    getBulkShipToContactNotificationMode(data);
  const notificationContentForResultCategory =
    BULK_SHIP_TO_CONTACT_NOTIFICATION_COPY[bulkOperationResultCategory];

  const secondary = (
    <>
      {notificationContentForResultCategory.getDescription(t, data)}
      {viewDetails}
    </>
  );

  return (
    <DataListItemText
      primary={bulkShipToContactPrimaryText(
        customerName,
        notificationContentForResultCategory.titleKey,
        t
      )}
      secondary={secondary}
    />
  );
};

export default BulkShipToContactNotification;
